/**
 * Sprint 5 C3 (EFISSUE-812) — parser for `build-complete` tagged messages.
 *
 * Walks the markdown of a Team Log message, detects the conventional section
 * headers planner + dev have been using all sprint, and returns a structured
 * representation that BuildCompleteCard can render directly. Falls back to a
 * `{ kind: "plain", text }` shape when none of the recognized headers are
 * present, so legacy build-completes still render via the standard row.
 *
 * Per planner directive `LdmnffWnzLk8HNaj7ls9` (seq 63):
 *   - Q1: commit hash → try the git-log fenced block first; fall back to
 *     the first `[0-9a-f]{7,40}` match anywhere in content
 *   - Q2: sprint label → strip the leading `## ` marker from the first
 *     `^## ` line; render the remainder verbatim
 *   - Q3: files → only extracted from a dedicated `### Files` header (no
 *     bullet-scanning heuristic across other sections)
 */

export type SectionVariant =
  | "shipped"
  | "files"
  | "deferred"
  | "shipConditions"
  | "actionRequired"
  | "commits";

export interface ParsedBuildSection {
  /** Display title for the section header (mono caps in the card UI). */
  title: string;
  /** Section kind — drives card styling (color, prefix glyph, etc.). */
  variant: SectionVariant;
  /** Raw markdown body of the section, leading + trailing whitespace stripped. */
  body: string;
}

export interface ParsedBuildStructured {
  kind: "structured";
  /** First `^## ` line of the message with the `## ` prefix stripped. */
  sprintLabel: string | null;
  /** Short commit hash (7+ hex chars). Sourced per Q1 priority. */
  commitHash: string | null;
  /** File paths extracted from a `### Files` section's bullet list. */
  files: string[];
  /** Sections in document order. */
  sections: ParsedBuildSection[];
}

export interface ParsedBuildPlain {
  kind: "plain";
  text: string;
}

export type ParsedBuild = ParsedBuildStructured | ParsedBuildPlain;

interface HeaderRule {
  variant: SectionVariant;
  title: string;
  patterns: RegExp[];
}

const HEADER_RULES: HeaderRule[] = [
  {
    variant: "shipped",
    title: "WHAT SHIPPED",
    patterns: [
      /^what ships$/i,
      /^what'?s live$/i,
      /^shipped$/i,
      /^what shipped$/i,
    ],
  },
  {
    variant: "files",
    title: "FILES",
    patterns: [/^files$/i, /^files to touch$/i],
  },
  {
    variant: "deferred",
    title: "DEFERRED",
    patterns: [
      /^deferred$/i,
      /^structural upgrades deferred$/i,
      /^scope carve-?out$/i,
    ],
  },
  {
    variant: "shipConditions",
    title: "SHIP CONDITIONS",
    patterns: [
      /^ship conditions$/i,
      /^visual verify$/i,
      /^after the deploy,? verify$/i,
    ],
  },
  {
    variant: "actionRequired",
    title: "ACTION REQUIRED",
    patterns: [
      /^travis action required$/i,
      /^pending travis action$/i,
      /^action required$/i,
    ],
  },
  {
    variant: "commits",
    title: "COMMITS",
    patterns: [/^git log( -\d+)?$/i, /^commits?$/i],
  },
];

const HASH_RE = /\b[0-9a-f]{7,40}\b/i;

function matchHeader(headerText: string): HeaderRule | null {
  const trimmed = headerText.trim();
  for (const rule of HEADER_RULES) {
    for (const pat of rule.patterns) {
      if (pat.test(trimmed)) return rule;
    }
  }
  return null;
}

function extractSprintLabel(content: string): string | null {
  for (const line of content.split("\n")) {
    const m = line.match(/^##\s+(.+?)\s*$/);
    if (m) return m[1].trim();
  }
  return null;
}

/**
 * Pull file paths out of a Files section body. Each bullet's first token
 * after the bullet marker is treated as a path; trailing markdown like
 * "— description" or backticks are stripped.
 */
function extractFiles(body: string): string[] {
  const files: string[] = [];
  for (const rawLine of body.split("\n")) {
    const line = rawLine.trim();
    if (!line) continue;
    const m = line.match(/^[-*+]\s+(.+)$/) || line.match(/^\d+\.\s+(.+)$/);
    if (!m) continue;
    let token = m[1].trim();
    // Strip backticks if the path is wrapped (e.g. `src/App.tsx`)
    token = token.replace(/^`+|`+$/g, "");
    // Stop at the first whitespace, em dash, en dash, or hyphen-with-spaces
    const stop = token.search(/\s+[—–-]\s+|\s+\(/);
    if (stop > 0) token = token.slice(0, stop);
    token = token.split(/\s+/)[0].replace(/^`+|`+$/g, "");
    if (token) files.push(token);
  }
  return files;
}

/**
 * First commit hash inside a fenced ``` block within the commits section,
 * if any. Matches the convention dev follows of pasting `git log -3` output
 * verbatim inside a fence.
 */
function commitHashFromCommitsSection(body: string): string | null {
  const fenceMatch = body.match(/```[\s\S]*?```/);
  const haystack = fenceMatch ? fenceMatch[0] : body;
  const m = haystack.match(HASH_RE);
  return m ? m[0] : null;
}

function commitHashAnywhere(content: string): string | null {
  const m = content.match(HASH_RE);
  return m ? m[0] : null;
}

/**
 * All distinct commit-like hashes anywhere in content, lowercased + deduped.
 * Used by the card renderer for "session summary" build-completes — posts
 * that roll up several tracks/commits into one message and so have no
 * single `commitHash` to show (see backlog seq #24552).
 */
export function extractAllCommitHashes(content: string): string[] {
  if (typeof content !== "string") return [];
  const matches = content.match(new RegExp(HASH_RE.source, "gi")) ?? [];
  return Array.from(new Set(matches.map((m) => m.toLowerCase())));
}

export function parseBuildComplete(content: string): ParsedBuild {
  if (typeof content !== "string" || content.trim().length === 0) {
    return { kind: "plain", text: content ?? "" };
  }

  const lines = content.split("\n");
  type Bucket = { rule: HeaderRule; lines: string[] };
  const buckets: Bucket[] = [];
  let current: Bucket | null = null;

  for (const line of lines) {
    const headerMatch = line.match(/^###\s+(.+?)\s*$/);
    if (headerMatch) {
      const rule = matchHeader(headerMatch[1]);
      if (rule) {
        current = { rule, lines: [] };
        buckets.push(current);
        continue;
      }
      // Recognised section ended by an unrecognised ### header — close the
      // current bucket so unrelated content doesn't bleed into the section.
      current = null;
      continue;
    }
    // A higher-level `## ` header also closes any open section.
    if (/^##\s+/.test(line)) {
      current = null;
      continue;
    }
    if (current) current.lines.push(line);
  }

  if (buckets.length === 0) {
    return { kind: "plain", text: content };
  }

  const sections: ParsedBuildSection[] = buckets.map((b) => ({
    title: b.rule.title,
    variant: b.rule.variant,
    body: b.lines.join("\n").trim(),
  }));

  // Files: only from a dedicated Files section (Q3).
  const filesSection = sections.find((s) => s.variant === "files");
  const files = filesSection ? extractFiles(filesSection.body) : [];

  // Commit hash: commits-section-first, then anywhere (Q1).
  const commitsSection = sections.find((s) => s.variant === "commits");
  let commitHash: string | null = null;
  if (commitsSection) {
    commitHash = commitHashFromCommitsSection(commitsSection.body);
  }
  if (!commitHash) {
    commitHash = commitHashAnywhere(content);
  }

  const sprintLabel = extractSprintLabel(content);

  return {
    kind: "structured",
    sprintLabel,
    commitHash,
    files,
    sections,
  };
}
